import { useState } from "react";
import { HorizontalLayout, VerticalLayout } from "./layout";
import { TextAreaForm } from "../atoms/input";
import { StandardButton } from "../atoms/buttons";
import { icon } from "../functions/icons";

const DIAGRAM_TEMPLATES = [
    { id: 1, name: "Modern", image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=200&h=200&fit=crop" },
    { id: 2, name: "Klassiek", image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=200&h=200&fit=crop" },
    { id: 3, name: "Flitsend", image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=200&h=200&fit=crop" },
];

export function AIDiagramGenerator({ onDiagramGenerated }) {
    const [description, setDescription] = useState("");
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [referenceImages, setReferenceImages] = useState([]);
    const [isGenerating, setIsGenerating] = useState(false);
    const [generatingStep, setGeneratingStep] = useState(0);

    const generatingSteps = [
        "Afbeeldingen analyseren...",
        "Diagram structuur bepalen...",
        "Visuele elementen genereren...",
        "Finishing touches..."
    ];

    function handleReferenceImageUpload(e) {
        const file = e.target.files[0];
        if (!file) return;

        const validTypes = ["image/jpeg", "image/png"];
        if (!validTypes.includes(file.type)) return;

        const imageUrl = URL.createObjectURL(file);
        setReferenceImages([...referenceImages, { file, preview: imageUrl }]);
    }

    function removeReferenceImage(index) {
        const newImages = [...referenceImages];
        URL.revokeObjectURL(newImages[index].preview);
        newImages.splice(index, 1);
        setReferenceImages(newImages);
    }

    function handleGenerate() {
        setIsGenerating(true);
        setGeneratingStep(0);

        const stepInterval = setInterval(() => {
            setGeneratingStep(prev => {
                if (prev >= generatingSteps.length - 1) {
                    clearInterval(stepInterval);
                    return prev;
                }
                return prev + 1;
            });
        }, 800);

        setTimeout(() => {
            clearInterval(stepInterval);
            setIsGenerating(false);
            setGeneratingStep(0);
            if (onDiagramGenerated) {
                onDiagramGenerated({
                    description,
                    template: selectedTemplate,
                    referenceImages: referenceImages.map(img => img.file)
                });
            }
        }, 3500);
    }

    if (isGenerating) {
        return (
            <VerticalLayout style={{ width: "380px", padding: "2rem 0", gap: "1.5rem", alignItems: "center" }}>
                <div
                    style={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        background: "linear-gradient(135deg, var(--primary-color) 0%, var(--primary-color-dark) 100%)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        animation: "pulse 1.5s ease-in-out infinite"
                    }}
                >
                    <span style={{ color: "white" }}>{icon("sparkles", 32)}</span>
                </div>
                <VerticalLayout style={{ gap: "0.5rem", alignItems: "center" }}>
                    <span className="standard-text bold" style={{ color: "var(--standard-text-color)" }}>
                        Diagram genereren
                    </span>
                    <span className="small-text" style={{ color: "var(--grey-600)" }}>
                        {generatingSteps[generatingStep]}
                    </span>
                </VerticalLayout>
                <div style={{ width: "100%", maxWidth: "200px" }}>
                    <div
                        style={{
                            width: "100%",
                            height: "4px",
                            backgroundColor: "var(--grey-200)",
                            borderRadius: "2px",
                            overflow: "hidden"
                        }}
                    >
                        <div
                            style={{
                                width: `${((generatingStep + 1) / generatingSteps.length) * 100}%`,
                                height: "100%",
                                backgroundColor: "var(--primary-color)",
                                borderRadius: "2px",
                                transition: "width 0.3s ease"
                            }}
                        />
                    </div>
                    <HorizontalLayout style={{ justifyContent: "space-between", marginTop: "0.25rem" }}>
                        <span className="xsmall-text" style={{ color: "var(--grey-500)" }}>
                            Stap {generatingStep + 1} van {generatingSteps.length}
                        </span>
                    </HorizontalLayout>
                </div>
                <style>{`
                    @keyframes pulse {
                        0%, 100% { transform: scale(1); opacity: 1; }
                        50% { transform: scale(1.05); opacity: 0.8; }
                    }
                `}</style>
            </VerticalLayout>
        );
    }

    return (
        <VerticalLayout style={{ width: "380px", gap: "0.75rem" }}>
            <VerticalLayout style={{ gap: "0.5rem" }}>
                <span className="small-text bold" style={{ color: "var(--standard-text-color)" }}>
                    Kies een template
                </span>
                <div
                    style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: "0.5rem"
                    }}
                >
                    {DIAGRAM_TEMPLATES.map((template) => (
                        <div
                            key={template.id}
                            onClick={() => setSelectedTemplate(template)}
                            style={{
                                position: "relative",
                                aspectRatio: "1",
                                borderRadius: "0.5rem",
                                overflow: "hidden",
                                cursor: "pointer",
                                border: selectedTemplate?.id === template.id
                                    ? "3px solid var(--primary-700)"
                                    : "2px solid var(--grey-300)",
                                transition: "border 0.15s ease"
                            }}
                        >
                            <img
                                src={template.image}
                                alt={template.name}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                            <div
                                style={{
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    padding: "0.25rem",
                                    background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                                    color: "white"
                                }}
                            >
                                <span className="xsmall-text">{template.name}</span>
                            </div>
                            {selectedTemplate?.id === template.id && (
                                <div
                                    style={{
                                        position: "absolute",
                                        bottom: "4px",
                                        right: "4px",
                                        width: "auto",
                                        height: "auto",
                                        borderRadius: "50%",
                                        backgroundColor: "var(--primary-color)",
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        color: "white"
                                    }}
                                >
                                    {icon("check", 20)}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </VerticalLayout>

            <VerticalLayout style={{ gap: "0.25rem" }}>
                <span className="small-text bold" style={{ color: "var(--standard-text-color)" }}>
                    Referentieafbeeldingen
                </span>
                <HorizontalLayout style={{ gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                    {referenceImages.map((img, index) => (
                        <div
                            key={index}
                            style={{
                                position: "relative",
                                width: "50px",
                                height: "50px",
                                borderRadius: "0.375rem",
                                overflow: "hidden",
                                border: "1px solid var(--grey-300)"
                            }}
                        >
                            <img
                                src={img.preview}
                                alt={`Referentie ${index + 1}`}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                            <div
                                onClick={() => removeReferenceImage(index)}
                                style={{
                                    position: "absolute",
                                    top: "2px",
                                    right: "2px",
                                    width: "16px",
                                    height: "16px",
                                    borderRadius: "50%",
                                    backgroundColor: "var(--grey-800)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    cursor: "pointer",
                                    color: "white"
                                }}
                            >
                                {icon("x", 10)}
                            </div>
                        </div>
                    ))}
                    <label
                        style={{
                            width: "50px",
                            height: "50px",
                            borderRadius: "0.375rem",
                            border: "1px dashed var(--grey-400)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            cursor: "pointer",
                            color: "var(--grey-500)",
                            backgroundColor: "var(--grey-100)"
                        }}
                    >
                        {icon("plus", 20)}
                        <input
                            type="file"
                            accept=".png,.jpg,.jpeg"
                            onChange={handleReferenceImageUpload}
                            style={{ display: "none" }}
                        />
                    </label>
                </HorizontalLayout>
            </VerticalLayout>

            <TextAreaForm
                title="Beschrijving"
                placeholder="Beschrijf wat je wilt dat AI moet doen"
                value={description}
                typeInput={setDescription}
                rows={3}
            />

            <StandardButton
                title="Genereer diagram"
                onClick={handleGenerate}
                disabled={!description.trim() || !selectedTemplate}
                icon="sparkles"
            />
        </VerticalLayout>
    );
}
